import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { minorFromInput } from '@/components/MoneyInput';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';

const INITIAL = { status: 'approved', preauthNo: '', approvedAmountMinor: '', notes: '' };

export function PreAuthRespondPage() {
  const { preAuthId = '' } = useParams<{ preAuthId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('preauth'));
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(INITIAL);
  const respond = useMutation({
    mutationFn: () =>
      put(`/insurance/pre-auths/${preAuthId}`, {
        status: form.status,
        preauthNo: form.preauthNo || undefined,
        approvedAmountMinor: form.approvedAmountMinor ? minorFromInput(form.approvedAmountMinor) : 0,
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success('Response recorded');
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-detail', preAuthId] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error('Could not record it', describeError(error)),
  });
  const dirty = !respond.isSuccess && JSON.stringify(form) !== JSON.stringify(INITIAL);

  return (
    <FormScreen
      title="Enter the payer’s approval"
      description="Only for an answer the payer gave outside NPHIES, such as a portal or a phone call. An answer that came through NPHIES cannot be typed over."
      icon={ClipboardCheck}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        respond.mutate();
      }}
    >
      <FormSection title={tr('billing', 'outcome')} icon={ClipboardCheck}>
        <Field label="Outcome" required className="sm:col-span-2">
          <ChoiceButtons
            layout="chips"
            value={form.status}
            onChange={(status) => setForm({ ...form, status })}
            options={['approved', 'partially_approved', 'rejected', 'expired'].map((status) => ({ value: status, label: humanize(status, 'preauth_status') }))}
          />
        </Field>
        {form.status.includes('approved') && (
          <>
            <Field label="Approval number" required hint="An approval without a number is not an approval">
              <Input value={form.preauthNo} onChange={(event) => setForm({ ...form, preauthNo: event.target.value })} />
            </Field>
            <Field label="Approved amount">
              <Input type="text" dir="ltr" inputMode="decimal" value={form.approvedAmountMinor} onChange={(event) => setForm({ ...form, approvedAmountMinor: event.target.value })} />
            </Field>
          </>
        )}
        <Field label="Note from the payer" className="sm:col-span-2">
          <Textarea rows={2} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        </Field>
      </FormSection>
      <FormActions submitLabel="Record" loading={respond.isPending} cancelTo={returnTo} />
    </FormScreen>
  );
}
